import React, { useEffect, useState } from "react"
import { Bar, Pie } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { getWalletDashboard } from "store/actions/dashboardAction"

const Wallet = ({ commonDateRange }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const dispatch = useDispatch()
  const { loading, walletDashboardData } = useSelector(state => state.Dashboard)

  const [isLoading, setIsLoading] = useState(true)
  const [barData, setBarData] = useState({
    labels: [],
    datasets: [],
  })

  const [pieData, setPieData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: ["#556ee6", "#D3A984"],
        hoverBackgroundColor: ["#556ee6", "#C09672"],
      },
    ],
  })

  const pieChartOptions = {
    plugins: {
      legend: {
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: tooltipItem => {
            const total = tooltipItem.dataset.data.reduce(
              (acc, value) => acc + value,
              0
            )
            const percentage = ((tooltipItem.raw / total) * 100).toFixed(1) // 1 decimal place
            return [
              `${tooltipItem.label}: ${tooltipItem.raw}`,
              `${t("Percentage")}: ${percentage}%`,
            ]
          },
        },
      },
    },
  }

  const getAllData = () => {
    const payload = {
      customerId: [],
      dateRange: commonDateRange || {},
    }

    dispatch(getWalletDashboard(payload))
  }

  useEffect(() => {
    getAllData()
  }, [commonDateRange])

  useEffect(() => {
    setIsLoading(true)
    if (walletDashboardData?.debitData) {
      const labels =
        walletDashboardData?.debitData?.map(item =>
          language === "sa" ? item?.source?.ar : item?.source?.en
        ) || []

      const data =
        walletDashboardData?.debitData?.map(item => item?.totalAmount) || []

      setPieData({
        labels,
        datasets: [
          {
            data,
            backgroundColor: ["#00e396", "#008ffb"],
            hoverBackgroundColor: ["#00e396", "#008ffb"],
          },
        ],
      })
    }

    if (walletDashboardData?.creditData) {
      const labels =
        walletDashboardData?.creditData?.map(item =>
          language === "sa" ? item?.source?.ar : item?.source?.en
        ) || []

      const datasets = [
        {
          label: t("Total Transactions"),
          data: walletDashboardData?.creditData?.map(item => item?.count),
          backgroundColor: "#556ee6",
        },
        {
          label: t("Total Amount"),
          data: walletDashboardData?.creditData?.map(item => item?.totalAmount),
          backgroundColor: "#84C5B1",
        },
      ]

      setBarData({ labels, datasets })
    }
    setTimeout(() => {
      setIsLoading(false)
    }, 500)
  }, [walletDashboardData, language])

  const walletCardTopData = [
    {
      id: 1,
      title: t("Total Open Wallet Balance"),
      value: walletDashboardData?.overallData?.openingWalletBalance ?? 0,
    },
    {
      id: 2,
      title: t("Total Wallet Credit"),
      value: walletDashboardData?.overallData?.totalCreditAmount ?? 0,
    },
    {
      id: 3,
      title: t("Total Wallet Spend/Consumed"),
      value: walletDashboardData?.overallData?.totalDebitAmount ?? 0,
    },
    {
      id: 4,
      title: t("Net Wallet Balance"),
      value: walletDashboardData?.overallData?.netWalletBalance ?? 0,
    },
  ]

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: context => `${context.dataset.label}: ${context.raw}`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }

  const getTotal = (data, field) => {
    return data
      ?.reduce((sum, item) => sum + (item?.[field] || 0), 0)
      ?.toFixed(2)
  }

  return (
    <div>
      <Row>
        {walletCardTopData?.map(item => (
          <Col key={item?.id} lg={3}>
            <Card className="card_100_24">
              <CardBody>
                <CardTitle className="text-center">{t(item?.title)}</CardTitle>
                {loading ? (
                  <ShimmerThumbnail height={30} rounded className="mb-0 pb-0" />
                ) : (
                  <div style={{ direction: "ltr" }}>
                    <h4 className="text-center">
                      {item.value} {t("SAR")}
                    </h4>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        <Col lg={6}>
          <Card className="full_height_card">
            <CardBody>
              <CardTitle>{t("Wallet Income Breakdown")}</CardTitle>
              {loading || isLoading ? (
                <ShimmerThumbnail height={300} rounded />
              ) : (
                <div style={{ direction: "ltr" }}>
                  <Bar
                    width={550}
                    height={400}
                    data={barData}
                    options={barOptions}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="full_height_card">
            <CardBody>
              <CardTitle>{t("Wallet Spend/Consumed Breakdown")}</CardTitle>
              {loading ? (
                <ShimmerThumbnail height={300} rounded />
              ) : (
                <div
                  style={{ direction: "ltr", maxHeight: 550 }}
                  className="pie_chart px-5"
                >
                  <Pie data={pieData} options={pieChartOptions} height={160} />
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {!loading && (
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle>{t("Wallet Income Breakdown")}</CardTitle>
                {!loading && walletDashboardData?.creditData?.length === 0 ? (
                  <p className="text-center my-3">{t("No data found")}</p>
                ) : (
                  <Table hover>
                    <thead className="table-light">
                      <tr>
                        <th>{t("Name")}</th>
                        <th>{t("Total Transactions")}</th>
                        <th>{t("Total Amount")}</th>
                        <th>{t("%")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletDashboardData?.creditData?.map(item => (
                        <tr key={item?._id?.giftCardId}>
                          <td>
                            {language === "sa"
                              ? item?.source?.ar
                              : item?.source?.en}
                          </td>
                          <td>{item?.count}</td>
                          <td>
                            {item?.totalAmount} {t("SAR")}
                          </td>
                          <td>{item?.percentShare?.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <th>{t("Total")}</th>
                        <th>
                          {getTotal(walletDashboardData?.creditData, "count")}{" "}
                        </th>
                        <th>
                          {getTotal(
                            walletDashboardData?.creditData,
                            "totalAmount"
                          )}{" "}
                          {t("SAR")}
                        </th>

                        <th>
                          {getTotal(
                            walletDashboardData?.creditData,
                            "percentShare"
                          )}
                          %
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      {!loading && (
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle>{t("Wallet Spend/Consumed Breakdown")}</CardTitle>
                {!loading && walletDashboardData?.debitData?.length === 0 ? (
                  <p className="text-center my-3">{t("No data found")}</p>
                ) : (
                  <Table hover>
                    <thead className="table-light">
                      <tr>
                        <th>{t("Name")}</th>
                        <th>{t("Total Transactions")}</th>
                        <th>{t("Total Amount")}</th>
                        <th>{t("%")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletDashboardData?.debitData?.map(item => (
                        <tr key={item?._id?.giftCardId}>
                          <td>
                            {language === "sa"
                              ? item?.source?.ar
                              : item?.source?.en}
                          </td>
                          <td>{item?.count}</td>
                          <td>
                            {item?.totalAmount} {t("SAR")}
                          </td>
                          <td>{item?.percentShare?.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <th>{t("Total")}</th>
                        <th>
                          {getTotal(walletDashboardData?.debitData, "count")}{" "}
                        </th>
                        <th>
                          {getTotal(
                            walletDashboardData?.debitData,
                            "totalAmount"
                          )}{" "}
                          {t("SAR")}
                        </th>

                        <th>
                          {getTotal(
                            walletDashboardData?.debitData,
                            "percentShare"
                          )}
                          %
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default Wallet